// ===== Production Message List Component =====

import React, { memo, useMemo } from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { Done, DoneAll, Schedule } from '@mui/icons-material';
import { sanitizeInput } from '../utils/ValidationUtils';

const MessageBubble = memo(({ message, isOwn, showAvatar, currentUser, receiver }) => {
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Done style={{ fontSize: '12px', color: '#666' }} />;
      case 'delivered':
        return <DoneAll style={{ fontSize: '12px', color: '#666' }} />;
      case 'read':
        return <DoneAll style={{ fontSize: '12px', color: '#4fc3f7' }} />;
      case 'pending':
        return <Schedule style={{ fontSize: '12px', color: '#666' }} />;
      default:
        return null;
    }
  };

  const renderMessageContent = () => {
    switch (message.type) {
      case 'image':
        return (
          <Box>
            <img 
              src={message.url} 
              alt="Shared image"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
              loading="lazy"
            />
            {message.text && message.text !== 'Shared image' && (
              <Typography 
                variant="body2" 
                style={{ marginTop: '8px', fontSize: '14px', lineHeight: '1.4' }}
              >
                {sanitizeInput(message.text)}
              </Typography>
            )}
          </Box>
        );
      
      case 'audio':
        return (
          <Box>
            <Box style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              padding: '8px',
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '18px' }}>🎵</span>
              <audio 
                controls 
                style={{ maxWidth: '200px' }}
                preload="metadata"
              >
                <source src={message.url} type="audio/webm" />
                <source src={message.url} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </Box>
            {message.text && message.text !== 'Shared audio' && (
              <Typography 
                variant="body2" 
                style={{ marginTop: '8px', fontSize: '14px', lineHeight: '1.4' }}
              >
                {sanitizeInput(message.text)}
              </Typography>
            )}
          </Box>
        );
      
      case 'file':
        return (
          <Box>
            <Chip 
              label={`📎 ${sanitizeInput(message.fileName)}`}
              variant="outlined"
              size="small"
              onClick={() => message.url && window.open(message.url, '_blank')}
              style={{ cursor: message.url ? 'pointer' : 'default' }}
            />
            {message.text && message.text !== 'Shared file' && (
              <Typography 
                variant="body2" 
                style={{ marginTop: '8px', fontSize: '14px', lineHeight: '1.4' }}
              >
                {sanitizeInput(message.text)}
              </Typography>
            )}
          </Box>
        );
      
      case 'system':
        return (
          <Typography 
            variant="caption" 
            style={{ 
              fontStyle: 'italic', 
              opacity: 0.7,
              fontSize: '12px'
            }}
          >
            {sanitizeInput(message.text)}
          </Typography>
        );
      
      default:
        return (
          <Typography 
            variant="body2" 
            style={{ 
              fontSize: '14px', 
              lineHeight: '1.4',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap'
            }}
          >
            {sanitizeInput(message.text)}
          </Typography>
        );
    }
  };

  if (message.type === 'system') {
    return (
      <Box 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          margin: '8px 0' 
        }}
      >
        <Chip 
          label={renderMessageContent()}
          size="small"
          variant="outlined"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.05)',
            fontSize: '11px'
          }}
        />
      </Box>
    );
  }

  return (
    <Box 
      style={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        marginBottom: '6px',
        alignItems: 'flex-end',
        gap: '6px'
      }}
    >
        {!isOwn && (
          <Avatar 
            style={{ 
              width: '28px', 
              height: '28px', 
              fontSize: '12px',
              backgroundColor: '#075E54',
              flexShrink: 0,
              marginBottom: '2px'
            }}
          >
            {(receiver?.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
        )}
      
      <Box
        style={{
          maxWidth: isOwn ? '75%' : '70%',
          padding: '10px 14px',
          borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          backgroundColor: isOwn ? '#DCF8C6' : 'white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          position: 'relative',
          wordBreak: 'break-word'
        }}
      >
        {!isOwn && message.senderName && (
          <Typography 
            variant="caption" 
            style={{ 
              color: '#128C7E', 
              fontWeight: '500',
              fontSize: '11px',
              marginBottom: '2px',
              display: 'block'
            }}
          >
            {sanitizeInput(message.senderName)}
          </Typography>
        )}
        
        {renderMessageContent()}
        
        <Box 
          style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            gap: '4px',
            marginTop: '4px' 
          }}
        >
          <Typography 
            variant="caption" 
            style={{ 
              fontSize: '11px', 
              color: '#666' 
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
          {isOwn && getStatusIcon(message.status)}
        </Box>
      </Box>
    </Box>
  );
});

const MessageList = ({ messages, currentUserId, messagesEndRef, isCallActive, currentUser, receiver }) => {
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    
    messages.forEach((message, index) => {
      const isOwn = message.sender === currentUserId;
      const prevMessage = messages[index - 1];
      const isFirstInGroup = !prevMessage || 
                            prevMessage.sender !== message.sender ||
                            (new Date(message.timestamp) - new Date(prevMessage.timestamp)) > 300000; // 5 minutes
      
      if (isFirstInGroup) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          sender: message.sender,
          isOwn,
          messages: [message],
          timestamp: message.timestamp
        };
      } else {
        currentGroup.messages.push(message);
      }
    });
    
    if (currentGroup) {
      groups.push(currentGroup);
    }
    
    return groups;
  }, [messages, currentUserId]);

  return (
    <Box
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#E5DDD5',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h40V20H20z'/%3E%3C/g%3E%3C/svg%3E")`,
        scrollBehavior: 'smooth',
        minHeight: 0
      }}
    >
      {messages.length === 0 ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          <Typography 
            variant="body2" 
            style={{ 
              color: '#666', 
              fontSize: '14px',
              textAlign: 'center'
            }}
          >
            Start a secure conversation...
          </Typography>
          <Typography 
            variant="caption" 
            style={{ 
              color: '#999', 
              fontSize: '12px',
              textAlign: 'center'
            }}
          >
            Messages are end-to-end encrypted
          </Typography>
        </Box>
      ) : (
        <>
          {groupedMessages.map((group, groupIndex) => {
            const isLastGroup = groupIndex === groupedMessages.length - 1;
            return (
              <Box key={groupIndex} style={{ marginBottom: isLastGroup ? '8px' : '12px' }}>
                {group.messages.map((message, messageIndex) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwn={group.isOwn}
                    showAvatar={messageIndex === 0 && !group.isOwn}
                    currentUser={currentUser}
                    receiver={receiver}
                  />
                ))}
              </Box>
            );
          })}
          
          {/* Typing indicator placeholder */}
          <Box ref={messagesEndRef} style={{ height: '1px' }} />
        </>
      )}
    </Box>
  );
};

export default memo(MessageList);
